// Page-level error boundary: a failed page keeps the top bar and shows an
// error (or offline) state with «Повторить», which refetches the data.
import i18n from '@/i18n';
import { nowMs } from '@/lib/time';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Component, Suspense, useEffect, useState, type ReactNode } from 'react';
import { pageByPath } from '@/app/nav';
import { StateCard } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { TopBar } from './TopBar';

type Props = { path: string; children: ReactNode; onReset: () => void };
class Boundary extends Component<Props, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  reset = () => { this.props.onReset(); this.setState({ error: null }); };
  render() {
    if (!this.state.error) return this.props.children;
    return <PageError path={this.props.path} error={this.state.error} onRetry={this.reset} />;
  }
}

export function RouteBoundary({ path, children }: { path: string; children: ReactNode }) {
  return <QueryErrorResetBoundary>{({ reset }) => <Boundary key={path} path={path} onReset={reset}>{children}</Boundary>}</QueryErrorResetBoundary>;
}

function PageError({ path, error, onRetry }: { path: string; error: Error; onRetry: () => void }) {
  const page = pageByPath(path);
  const offline = error instanceof ApiError && error.kind === 'offline';
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = `${page.title} — Orca`; }, [page.title]);
  const retry = () => { setBusy(true); window.setTimeout(onRetry, 700); };
  return (
    <>
      <div className="panel err-panel">
        <Suspense fallback={<div className="top" />}><TopBar /></Suspense>
        <section className="hero err-hero">
          <h1 className="h-title">{page.title}</h1>
          <StateCard onLight kind={offline ? 'offline' : 'error'}
            title={offline ? i18n.t('nav:error.offline') : i18n.t('nav:error.page')}
            text={offline
              ? i18n.t('nav:error.offlineText')
              : i18n.t('nav:error.pageText')}
            action={{ label: busy ? i18n.t('nav:error.retrying') : i18n.t('common:action.retry'), busy, onClick: retry }}
            second={{ label: i18n.t('nav:error.home'), to: '/' }} />
          {!offline && <p className="err-code">{i18n.t('nav:error.code')} {error instanceof ApiError ? 'API_TIMEOUT' : 'RENDER'} · {new Date(nowMs()).toISOString().slice(11, 16)} UTC</p>}
        </section>
      </div>
    </>
  );
}

/** Global strip when the browser goes offline (or the dev switch says so). */
export function OfflineBanner({ forced }: { forced: boolean }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (online && !forced) return null;
  return <div className="offline-ban" role="status"><i aria-hidden="true" />{i18n.t('nav:error.offlineBanner')}</div>;
}
